import { useCallback, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Play } from 'lucide-react'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '@/components/ui/dialog'
import { m } from '@/lib/paraglide/messages'
import { cn } from '@/lib/utils'

/** Một tệp đính kèm có thể xem được: ảnh hoặc video. */
export interface MediaItem {
    id: string
    url: string
    name?: string
    /** MIME của tệp. `video/*` render `<video>`, còn lại coi là ảnh. */
    mimeType?: string
}

const isVideo = (item: MediaItem) => !!item.mimeType?.startsWith('video/')

/** Nhãn thay thế của một tệp: tên tệp nếu có, không thì nhãn chung. */
const mediaLabel = (item: MediaItem) =>
    item.name || (isVideo(item) ? m.media_video() : m.media_image())

interface ThumbStripProps {
    items: MediaItem[]
    /** Người dùng bấm vào tệp thứ `index` — chỗ gọi mở lightbox tại vị trí đó. */
    onOpen: (index: number) => void
    /** Class cỡ ô thumbnail, mặc định `size-10` (đủ nhỏ để nằm trong ô bảng). */
    thumbClassName?: string
    /**
     * Số ô tối đa được chiếm chỗ. Vượt quá thì ô cuối thành "+N" gộp phần còn
     * lại — bấm vào vẫn mở popup có ĐỦ tệp, chỉ dải thumbnail bị cắt. Bỏ trống
     * là liệt kê hết (màn chi tiết dùng kiểu này vì không lo dòng cao).
     */
    maxThumbs?: number
    className?: string
}

/**
 * Dải thumbnail của mọi tệp đính kèm. Không tự mở popup: chỗ gọi giữ một
 * `MediaLightbox` DUY NHẤT cho cả màn thay vì mỗi dòng một Dialog — bảng danh
 * sách trăm dòng mà mỗi dòng một Dialog là trăm cây Radix dựng vô ích.
 */
export function MediaThumbStrip({
    items,
    onOpen,
    thumbClassName = 'size-10',
    maxThumbs,
    className,
}: ThumbStripProps) {
    if (items.length === 0) return null

    // Chỉ cắt khi THỰC SỰ tràn: đúng bằng maxThumbs thì hiện hết, không đổi ô
    // cuối thành "+1" (giấu một tệp để hiện một ô báo có một tệp là vô nghĩa).
    const overflow = maxThumbs !== undefined && items.length > maxThumbs
    const shown = overflow ? items.slice(0, maxThumbs - 1) : items
    const hidden = items.length - shown.length

    return (
        <div className={cn('flex flex-wrap gap-1.5', className)}>
            {shown.map((item, index) => (
                <button
                    key={item.id}
                    type="button"
                    data-testid="media-thumb"
                    onClick={() => onOpen(index)}
                    aria-label={mediaLabel(item)}
                    className={cn(
                        'relative cursor-pointer overflow-hidden rounded-md border bg-muted/30',
                        'transition-colors hover:border-primary focus-visible:border-primary',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        thumbClassName,
                    )}
                >
                    {isVideo(item) ? (
                        <>
                            {/* `preload="none"`: thẻ video KHÔNG lười tải như
                                ảnh (`loading="lazy"`), nên `metadata` nghĩa là
                                mỗi dòng trong bảng 100 dòng bắn một request
                                ngay lúc mở màn — video quay bằng điện thoại
                                thường để moov atom ở cuối file nên request đó
                                kéo gần hết file. Badge play bên dưới mới là thứ
                                cho biết đây là video, không cần khung hình. */}
                            <video
                                src={item.url}
                                className="size-full object-cover"
                                muted
                                playsInline
                                preload="none"
                            />
                            <span
                                data-testid="media-thumb-play"
                                aria-hidden="true"
                                className="absolute inset-0 flex items-center justify-center bg-black/25 text-white"
                            >
                                <Play className="size-4 fill-current" />
                            </span>
                        </>
                    ) : (
                        <img
                            src={item.url}
                            alt={mediaLabel(item)}
                            loading="lazy"
                            className="size-full object-cover"
                        />
                    )}
                </button>
            ))}

            {overflow && (
                <button
                    type="button"
                    data-testid="media-thumb-more"
                    onClick={() => onOpen(shown.length)}
                    aria-label={m.media_more_label({ count: hidden })}
                    className={cn(
                        'flex cursor-pointer items-center justify-center rounded-md border bg-muted',
                        'text-xs font-medium tabular-nums text-muted-foreground',
                        'transition-colors hover:border-primary hover:text-foreground',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        thumbClassName,
                    )}
                >
                    {m.media_more_count({ count: hidden })}
                </button>
            )}
        </div>
    )
}

interface LightboxProps {
    items: MediaItem[]
    /** Vị trí đang xem; `null` là đóng. */
    index: number | null
    onIndexChange: (index: number) => void
    onClose: () => void
}

/**
 * Popup xem ảnh/video cỡ lớn, chuyển qua lại bằng nút hoặc phím ←/→ (Esc đóng
 * do Radix Dialog lo). Chuyển vòng tròn: ở tệp cuối bấm Sau về tệp đầu — người
 * xem không phải quay ngược cả dải để xem lại tấm đầu.
 */
export function MediaLightbox({
    items,
    index,
    onIndexChange,
    onClose,
}: LightboxProps) {
    const total = items.length
    const open = index !== null && index >= 0 && index < total

    const step = useCallback(
        (delta: number) => {
            if (index === null || total === 0) return
            onIndexChange((index + delta + total) % total)
        },
        [index, total, onIndexChange],
    )

    useEffect(() => {
        if (!open || total < 2) return
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') step(1)
            else if (e.key === 'ArrowLeft') step(-1)
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [open, total, step])

    if (!open) return null
    const item = items[index]
    const label = mediaLabel(item)

    return (
        <Dialog open onOpenChange={(next) => !next && onClose()}>
            <DialogContent className="max-w-3xl gap-3">
                {/* Radix đòi tiêu đề/mô tả cho mọi dialog; ở đây nội dung là
                    chính nên chỉ đọc cho screen reader, không chiếm chỗ. */}
                <DialogTitle className="sr-only">{label}</DialogTitle>
                <DialogDescription className="sr-only">
                    {m.media_preview_hint()}
                </DialogDescription>

                <div
                    data-testid="media-lightbox-figure"
                    className="relative flex min-h-40 items-center justify-center overflow-hidden rounded-md bg-muted/40"
                >
                    {isVideo(item) ? (
                        <video
                            key={item.id}
                            src={item.url}
                            controls
                            playsInline
                            className="max-h-[70svh] w-full"
                        />
                    ) : (
                        <img
                            key={item.id}
                            src={item.url}
                            alt={label}
                            className="max-h-[70svh] w-full object-contain"
                        />
                    )}

                    {total > 1 && (
                        <>
                            <NavButton
                                side="left"
                                label={m.common_prev()}
                                onClick={() => step(-1)}
                            />
                            <NavButton
                                side="right"
                                label={m.common_next()}
                                onClick={() => step(1)}
                            />
                        </>
                    )}
                </div>

                <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate text-muted-foreground">
                        {label}
                    </span>
                    {total > 1 && (
                        <span
                            data-testid="media-lightbox-counter"
                            className="shrink-0 tabular-nums text-muted-foreground"
                        >
                            {index + 1} / {total}
                        </span>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}

function NavButton({
    side,
    label,
    onClick,
}: {
    side: 'left' | 'right'
    label: string
    onClick: () => void
}) {
    const Icon = side === 'left' ? ChevronLeft : ChevronRight
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            className={cn(
                'absolute top-1/2 flex size-11 -translate-y-1/2 cursor-pointer items-center justify-center',
                'rounded-full bg-black/55 text-white transition-colors hover:bg-black/75',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white',
                side === 'left' ? 'left-2' : 'right-2',
            )}
        >
            <Icon className="size-5" />
        </button>
    )
}
